import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  BookOpen, Download, Trash2, Plus, Upload, FolderOpen, Edit, Save, 
  Search, FileText, Eye, X, Loader2, Filter, BookMarked, FileSpreadsheet, ScrollText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import TwoStepUpload from '../../components/TwoStepUpload';
import useAuthStore from '../../store/authStore';

// Modern category configuration with icons and colors
const CATEGORIES = {
  'Books': { icon: BookMarked, gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600' },
  'Reports': { icon: FileSpreadsheet, gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
  'Policies': { icon: ScrollText, gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600' },
  'Publications': { icon: FileText, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-600' },
  'Forms': { icon: FileText, gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-600' },
  'Guides': { icon: BookOpen, gradient: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-600' },
  'Resources': { icon: FolderOpen, gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-500', light: 'bg-slate-100', text: 'text-slate-600' },
  'Other': { icon: FileText, gradient: 'from-gray-500 to-slate-600', bg: 'bg-gray-500', light: 'bg-gray-100', text: 'text-gray-600' },
};

const getCategoryConfig = (cat) => CATEGORIES[cat] || CATEGORIES['Other'];

const Library = () => {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [stagedFileUrl, setStagedFileUrl] = useState(null);
  const [stagedFileName, setStagedFileName] = useState(null);
  
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFile, setEditFile] = useState(null);

  const { getRootProps: getEditRootProps, getInputProps: getEditInputProps, isDragActive: isEditDragActive } = useDropzone({
    maxFiles: 1,
    onDrop: (acceptedFiles) => setEditFile(acceptedFiles[0]),
  });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const response = await api.get('/library');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load library items');
    } finally {
      setLoading(false);
    }
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchQuery, selectedCategory]);

  // Group by category
  const groupedItems = useMemo(() => {
    return filteredItems.reduce((acc, item) => {
      const cat = item.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [filteredItems]);

  // Stats
  const stats = useMemo(() => ({
    total: items.length,
    books: items.filter(i => i.category === 'Books').length,
    reports: items.filter(i => i.category === 'Reports').length,
    policies: items.filter(i => i.category === 'Policies').length,
  }), [items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stagedFileUrl) { toast.error('Please upload and save a file first'); return; }
    setLoading(true);
    try {
      await api.post('/library', { title, category, file_url: stagedFileUrl, description, uploaded_by: user.user_id });
      toast.success('Document added successfully');
      setDialogOpen(false);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSaved = (fileUrl, fileName) => {
    setStagedFileUrl(fileUrl);
    setStagedFileName(fileName);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditCategory(item.category);
    setEditDescription(item.description || '');
    setEditFile(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    setLoading(true);
    try {
      let file_url = editingItem.file_url;
      if (editFile) {
        const formData = new FormData();
        formData.append('file', editFile);
        const uploadResponse = await api.post('/files/upload', formData);
        file_url = uploadResponse.data.file_url;
      }
      await api.put(`/library/${editingItem.id}`, { title: editTitle, category: editCategory, description: editDescription, file_url });
      toast.success('Document updated successfully');
      setEditDialogOpen(false);
      setEditingItem(null);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/library/${id}`);
      toast.success('Document deleted');
      loadItems();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const resetForm = () => { setTitle(''); setCategory(''); setDescription(''); setStagedFileUrl(null); setStagedFileName(null); };

  const openPreview = async (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(item.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.file_url, item.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-library-page">
      <BackButton to="/admin" />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 p-4 rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">Library Management</h1>
            <p className="text-slate-400">Books, reports, policies, and publications</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg">
              <Plus className="w-4 h-4 mr-2" />Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
            <DialogHeader><DialogTitle className="text-white">Add Library Document</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title..." required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select value={category} onValueChange={setCategory} required>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.keys(CATEGORIES).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Description (Optional)</Label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Upload File (Two-Step: Upload → Save)</Label>
                <TwoStepUpload 
                  onSaveComplete={handleFileSaved}
                  onCancel={() => { setStagedFileUrl(null); setStagedFileName(null); }}
                  label="Upload Document"
                  acceptedTypes={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'application/vnd.ms-excel': ['.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  }}
                />
                {stagedFileUrl && (
                  <p className="text-sm text-emerald-400 mt-2">✓ File saved: {stagedFileName}</p>
                )}
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={loading || !stagedFileUrl} className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600">{loading ? 'Adding...' : 'Add Document'}</Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-400 text-sm font-medium">Total Documents</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <BookOpen className="w-8 h-8 text-violet-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium">Books</p>
              <p className="text-2xl font-bold text-slate-900">{stats.books}</p>
            </div>
            <BookMarked className="w-8 h-8 text-blue-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-sm font-medium">Reports</p>
              <p className="text-2xl font-bold text-slate-900">{stats.reports}</p>
            </div>
            <FileSpreadsheet className="w-8 h-8 text-emerald-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-sm font-medium">Policies</p>
              <p className="text-2xl font-bold text-slate-900">{stats.policies}</p>
            </div>
            <ScrollText className="w-8 h-8 text-amber-500/50" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search documents..." className="pl-10 bg-slate-700 border-slate-600 text-white" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all">All Categories</SelectItem>
              {Object.keys(CATEGORIES).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setSelectedCategory('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === 'all' ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>All</button>
        {Object.keys(CATEGORIES).map(cat => {
          const config = getCategoryConfig(cat);
          const count = items.filter(i => i.category === cat).length;
          return (
            <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${selectedCategory === cat ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg` : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
              <span>{cat}</span>
              <span className={`text-xs ${selectedCategory === cat ? 'bg-white/20' : 'bg-slate-600'} px-2 py-0.5 rounded-full`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Edit Document</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select value={editCategory} onValueChange={setEditCategory} required>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">{Object.keys(CATEGORIES).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Replace File (Optional)</Label>
              <div {...getEditRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${isEditDragActive ? 'border-violet-500 bg-violet-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
                <input {...getEditInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                {editFile ? <p className="text-sm text-violet-400">New: {editFile.name}</p> : <p className="text-sm text-slate-400">Drop to replace</p>}
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600"><Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-slate-800 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="font-semibold text-white">{previewItem.title}</h3>
                <p className="text-sm text-slate-400">{previewItem.category}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewItem)} className="bg-violet-600 hover:bg-violet-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-900">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Grid by Category */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
      ) : Object.keys(groupedItems).length > 0 ? (
        <div className="space-y-8">
          {Object.keys(groupedItems).map((cat) => {
            const config = getCategoryConfig(cat);
            const IconComponent = config.icon;
            return (
              <div key={cat}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`bg-gradient-to-r ${config.gradient} p-2 rounded-lg shadow-lg`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-serif font-semibold text-slate-900">{cat}</h2>
                  <span className="text-sm text-slate-500">({groupedItems[cat].length})</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedItems[cat].map((item) => (
                    <div key={item.id} className={`group bg-slate-800 rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-violet-500/50 transition-all duration-300 hover:shadow-xl`}>
                      <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`bg-gradient-to-br ${config.gradient} p-3 rounded-xl shadow-lg`}>
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEditDialog(item)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg" title="Edit"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(item.id, item.title)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-violet-400 transition-colors">{item.title}</h3>
                        {item.description && <p className="text-sm text-slate-400 mb-4 line-clamp-2">{item.description}</p>}
                        <div className="flex space-x-2">
                          <Button onClick={() => openPreview(item)} variant="outline" size="sm" data-testid="preview-btn" className="flex-1 bg-slate-700 text-white border-slate-500 hover:bg-slate-600 hover:text-white hover:border-violet-400">
                            <Eye className="w-4 h-4 mr-1" />Preview
                          </Button>
                          <Button onClick={() => handleDownload(item)} size="sm" className={`flex-1 bg-gradient-to-r ${config.gradient}`}>
                            <Download className="w-4 h-4 mr-1" />Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">{selectedCategory === 'all' ? 'No documents in the library yet' : `No documents in ${selectedCategory}`}</p>
          <p className="text-sm text-slate-500 mt-2">Click "Add Document" to upload your first resource</p>
        </div>
      )}
    </div>
  );
};

export default Library;
